import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { LeadsEmpty } from '@images';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import { MetaIntegrationProvider } from '@features/lead-management/components/lead-settings/types';
import { MetaSyncItem } from '@features/lead-management/metaSyncSlice';
import { LeadImportOption } from '@features/lead-management/types/common';
import { META_SYNC_STATUS } from '@features/lead-management/utils/metaIntegrationHelpers';
import Head from '@layouts/head/Head';
import useContentTopOffset from '@layouts/main-layout/hooks/useContentTopOffset';

import LeadBulkUploadModal from './LeadBulkUploadModal';

import SetupMetaIntegrationModal from '../../components/lead-settings/SetupMetaIntegrationModal';

const getLeadImportGridCols = (count: number) => {
  if (count === 1) return 'md:grid-cols-1';
  if (count % 3 === 0) return 'md:grid-cols-3';
  return 'md:grid-cols-2';
};

const LeadListHeading = () => (
  <div className="flex shrink-0 items-center gap-4 pt-6">
    <div className="text-2xl font-bold text-content">Leads</div>
  </div>
);

interface LeadListEmptyBodyProps {
  onCsvUpload: () => void;
  onAddManually: () => void;
  showMetaOption: boolean;
  onSyncFromMeta: () => void;
  metaProvider?: MetaIntegrationProvider | null;
  restrictLeadCreationForAgent: boolean;
}

const LeadListEmptyBody = ({
  onCsvUpload,
  onAddManually,
  showMetaOption,
  onSyncFromMeta,
  metaProvider,
  restrictLeadCreationForAgent
}: LeadListEmptyBodyProps) => {
  const syncs = useSelector(
    (state: any) => state.metaSync?.items || []
  ) as MetaSyncItem[];

  const isMetaSyncing = syncs.some(
    (sync) => sync.status === META_SYNC_STATUS.SYNCING
  );

  const getMetaButtonLabel = () => {
    if (isMetaSyncing) return 'Syncing...';
    return metaProvider ? 'Sync' : 'Connect';
  };

  const leadImportOptions: LeadImportOption[] = [
    {
      id: 'csv-upload',
      iconName: 'doc-upload',
      iconClassName: 'h-25.5 w-29',
      title: 'CSV/Excel Upload',
      description: 'Upload leads from spreadsheet files',
      buttonLabel: 'Start Import',
      onClick: onCsvUpload
    },
    {
      id: 'manual-add',
      iconName: 'manual-add',
      iconClassName: 'size-25.5',
      title: 'Add manually',
      description: 'Add individual leads manually',
      buttonLabel: 'Add Manually',
      onClick: onAddManually
    },
    ...(showMetaOption
      ? [
          {
            id: 'lead-via-meta',
            iconName: 'lead-via-meta',
            iconClassName: 'h-25.5 w-29',
            title: 'Sync from Meta',
            description: 'Automatically import leads from Meta forms',
            buttonLabel: getMetaButtonLabel(),
            onClick: onSyncFromMeta,
            disabled: isMetaSyncing || metaProvider?.is_active === false,
            buttonClassName: 'w-fit min-w-[136px]',
            disabledTooltip:
              metaProvider?.is_active === false
                ? 'Lead sync is disabled in Settings. Turn it on to sync now'
                : undefined
          }
        ]
      : [])
  ].filter(
    (option) => !restrictLeadCreationForAgent || option.id === 'manual-add'
  );

  return (
    <div className="flex min-h-[50vh] w-full flex-col items-center justify-center px-4 py-10">
      <div
        className={`w-full rounded-2xl border border-cofeeGreyBlue4 bg-white p-8 shadow-sm md:p-10 ${
          leadImportOptions.length % 3 === 0 ? 'max-w-5xl' : 'max-w-3xl'
        }`}
      >
        <div className="mb-8 flex flex-col items-center gap-2 text-center md:mb-10">
          <h1 className="text-xl font-semibold text-cofeeNeutral10 md:text-2xl">
            How would you like to import leads?
          </h1>
          <p className="max-w-xl text-base text-cofeeNeutral7">
            Choose an import method to begin adding leads
          </p>
        </div>

        <div
          className={`grid justify-center gap-4 md:gap-6 ${getLeadImportGridCols(
            leadImportOptions.length
          )}`}
        >
          {leadImportOptions.map((option) => (
            <div
              key={option.id}
              className="flex min-h-full flex-col items-center gap-6 rounded-xl border border-cofeeGreyBlue4 bg-cofeeNeutralBg p-6 md:p-8"
            >
              <div
                className={`flex items-center justify-center ${option.iconClassName}`}
              >
                <Icon
                  name={option.iconName}
                  size="none"
                  className={option.iconClassName}
                />
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="text-lg font-semibold text-cofeeNeutral10">
                  {option.title}
                </div>
                <div className="text-sm text-cofeeNeutral7 md:text-base">
                  {option.description}
                </div>
              </div>
              <div className="mt-auto w-full">
                <Button
                  variant="secondary"
                  label={option.buttonLabel}
                  handleClick={option.onClick}
                  className={`w-full ${option.buttonClassName ?? ''}`}
                  disabled={option.disabled}
                  tooltipText={option.disabledTooltip}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export interface LeadListEmptyViewProps {
  organizationId: string;
  onAddManually: () => void;
  showMetaOption: boolean;
  onSyncFromMeta: () => void;
  onCsvUploadSuccess: () => void;
  onMetaSetupConfirm: (accessToken: string, date: Date) => void;
  metaProvider?: MetaIntegrationProvider | null;
  restrictLeadCreationForAgent: boolean;
  canCreateLead: boolean;
  isMetaSetupModalOpen: boolean;
  onMetaSetupModalClose: () => void;
}

const LeadListEmptyView = ({
  organizationId,
  onAddManually,
  showMetaOption,
  onSyncFromMeta,
  onCsvUploadSuccess,
  onMetaSetupConfirm,
  metaProvider,
  restrictLeadCreationForAgent,
  canCreateLead,
  isMetaSetupModalOpen,
  onMetaSetupModalClose
}: LeadListEmptyViewProps) => {
  const contentTopOffset = useContentTopOffset();
  const emptyShellMinHeight = `calc(var(--app-vh) - ${contentTopOffset}px - 5.25rem)`;

  const [isCsvUploadModalOpen, setIsCsvUploadModalOpen] = useState(false);

  if (!canCreateLead) {
    return (
      <div
        className="relative flex flex-1 flex-col overflow-hidden"
        style={{ minHeight: emptyShellMinHeight }}
      >
        <Head title="Leads" />
        <LeadListHeading />
        <div className="flex flex-1 flex-col items-center justify-center">
          <LeadsEmpty className="size-63" />
          <div className="mt-8 text-2xl font-bold">No leads yet</div>
          <div className="mt-2 text-sm font-normal text-grey">
            Leads assigned to you will appear here
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative flex flex-1 flex-col ${
        restrictLeadCreationForAgent ? 'overflow-hidden' : ''
      }`}
      style={
        restrictLeadCreationForAgent
          ? { minHeight: emptyShellMinHeight }
          : undefined
      }
    >
      <Head title="Leads" />
      <div
        className={`flex min-h-0 flex-1 flex-col ${
          restrictLeadCreationForAgent ? 'overflow-hidden' : ''
        }`}
      >
        {restrictLeadCreationForAgent && <LeadListHeading />}
        <div
          className={`flex min-h-0 flex-1 flex-col ${
            restrictLeadCreationForAgent
              ? 'overflow-hidden'
              : 'items-center justify-center'
          }`}
        >
          <LeadListEmptyBody
            onAddManually={onAddManually}
            onCsvUpload={() => setIsCsvUploadModalOpen(true)}
            showMetaOption={showMetaOption}
            onSyncFromMeta={onSyncFromMeta}
            metaProvider={metaProvider}
            restrictLeadCreationForAgent={restrictLeadCreationForAgent}
          />
        </div>
      </div>
      <LeadBulkUploadModal
        isOpen={isCsvUploadModalOpen}
        handleCancel={() => setIsCsvUploadModalOpen(false)}
        onCreateSuccess={() => {
          setIsCsvUploadModalOpen(false);
          onCsvUploadSuccess();
        }}
        organizationId={organizationId}
      />
      <SetupMetaIntegrationModal
        isOpen={isMetaSetupModalOpen}
        onClose={onMetaSetupModalClose}
        onConfirm={onMetaSetupConfirm}
      />
    </div>
  );
};

export default LeadListEmptyView;
